import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { NAV_LINKS, COMPANY } from "@/lib/siteData";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F6F5F2]/85 backdrop-blur-xl border-b border-[#D2D0C9]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-fz flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 grid place-items-center bg-[#D05322] text-[#F6F5F2] font-display text-base">
            FE
          </span>
          <span className="font-display text-lg md:text-xl tracking-tight text-stone-900">
            Fitzgerald<span className="text-[#D05322]">.</span>Earthworks
            <span className="text-stone-500 text-xs ml-1.5 align-top">LTD</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.slice(0, -1).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-stone-900"
                    : "text-stone-600 hover:text-stone-900"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-phone-link"
            className="text-sm font-bold tracking-wide text-stone-900 hover:text-[#D05322]"
          >
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="btn-primary !px-5 !py-2.5 !text-xs"
          >
            Get a Quote
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2 text-stone-900"
        >
          {open ? <X size={26} weight="bold" /> : <List size={26} weight="bold" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden bg-[#F6F5F2] border-t border-[#D2D0C9] transition-[max-height] duration-300 ${
          open ? "max-h-[600px]" : "max-h-0"
        }`}
      >
        <div className="container-fz py-6 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `py-3 px-2 text-base font-medium border-b border-[#D2D0C9] ${
                  isActive ? "text-[#D05322]" : "text-stone-900"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <a
            href={COMPANY.phoneHref}
            data-testid="mobile-nav-phone"
            className="mt-4 btn-primary"
          >
            Call {COMPANY.phone}
          </a>
        </div>
      </div>
    </header>
  );
}
